import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, MapPin } from 'lucide-react';

/**
 * HeroSection — sits OVER the global BackgroundVideo. Sticky for ~250vh
 * so the hero copy progresses through 4 synced states while the global
 * video continues to scrub through the buildings clip.
 */
const HeroSection = () => {
  const { t } = useTranslation();
  const HERO_STATES = t('hero.states', { returnObjects: true });
  const wrapperRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mob = window.matchMedia('(max-width: 768px)');
    setIsMobile(mob.matches);
    const h = (e) => setIsMobile(e.matches);
    mob.addEventListener('change', h);
    return () => mob.removeEventListener('change', h);
  }, []);

  useEffect(() => {
    const wrap = wrapperRef.current;
    if (!wrap) return;
    const onScroll = () => {
      const rect = wrap.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      setProgress(total > 0 ? scrolled / total : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const activeState = Math.min(HERO_STATES.length - 1, Math.floor(progress * HERO_STATES.length));

  return (
    <section ref={wrapperRef} id="top" className="relative" style={{ height: isMobile ? '100vh' : '300vh' }}>
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Top meta */}
        <div className="absolute top-24 md:top-28 left-0 right-0 z-10">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between text-[11px] tracking-[0.22em] uppercase text-[#9A9A8E]">
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#5FE0C8] animate-pulse" /> {t('hero.topMetaLeft')}</span>
            <span className="hidden md:inline">{t('hero.topMetaRight')}</span>
          </div>
        </div>

        {/* Hero copy */}
        <div className="absolute inset-0 z-10 flex items-end md:items-center">
          <div className="max-w-[1440px] w-full mx-auto px-6 md:px-10 pb-24 md:pb-0">
            <div className="max-w-[820px]">
              <div className="relative h-[200px] md:h-[260px]">
                {HERO_STATES.map((s, i) => (
                  <div
                    key={s.title}
                    className="absolute inset-0 transition-all duration-700"
                    style={{ opacity: i === activeState ? 1 : 0, transform: `translateY(${i === activeState ? 0 : 20}px)` }}
                  >
                    <h1 className="font-display text-[44px] md:text-[78px] leading-[0.95] tracking-tight text-[#ECE9DF]" style={{ textShadow: '0 2px 30px rgba(0,0,0,0.5)' }}>
                      {s.title}{' '}
                      <span className="font-serif-i text-[#5FE0C8]">{s.accent}</span>
                    </h1>
                    <p className="mt-5 text-[18px] md:text-[22px] text-[#C9C5B8] max-w-[560px] leading-snug" style={{ textShadow: '0 1px 18px rgba(0,0,0,0.6)' }}>{s.sub}</p>
                  </div>
                ))}
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <a href="#contact" data-testid="hero-cta-strategy" className="btn-mint px-6 py-3.5 rounded-full text-[14px] inline-flex items-center gap-2">
                  {t('hero.ctaStrategy')}
                </a>
                <a href="#services" data-testid="hero-cta-services" className="btn-ghost px-6 py-3.5 rounded-full text-[14px] inline-flex items-center gap-2 backdrop-blur-sm">
                  {t('hero.ctaServices')}
                </a>
              </div>
              <div className="mt-10 hidden md:flex items-center gap-6 text-[12px] text-[#9A9A8E]">
                <span className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {t('hero.servicing')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-6 md:left-10 z-10 hidden md:flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#9A9A8E]">
          <ArrowDown className="w-3.5 h-3.5" /> {t('hero.scroll')}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
